import React, { useEffect, useState } from 'react';
import { Box, Stack, Button } from '@mui/material';
import { useParams } from 'react-router-dom';
import ContainerPainel from 'components/container_painel';
import { FormSimHorizontalStepper } from 'components/forms/form_sim_horizontal_stepper';
import { getFormularioSimByUUID } from 'api/forms/pacote_formularios.api';
import { getListUsersByFilters } from 'api/user/user.api';
import Step1SimView from './steps/step1_view';
import Step2SimView from './steps/step2_view';
import Step3SimView from './steps/step3_view';
import Step4SimView from './steps/step4_view';
import Step5SimView from './steps/step5_view';
import Step6SimView from './steps/step6_view';

// Step 1: SIM (Serviço de Inspeção Municipal)
const emptyStep1 = {
    referenceYear: new Date().getFullYear(),
    hasSim: null,
    simActive: null,
    simEstablishments: 0,
    hasVet: null,
    joinedSusaf: null,
    wantsSusaf: null,
};

// Step 5: Certificação/Identificação
const emptyStep5Certificacao = {
    hasCertification: null,
    certificationSoldInFairs: null,
    hasMunicipalSeal: null,
    municipalSealName: '',
    wantsMunicipalSeal: null,
    knowsHowToCreateSeal: null,
    referenceYear: new Date().getFullYear(),
};

// Step 2: Feiras da Agricultura Familiar (use emptyStep3 para Step2)
const emptyStep2 = {
    referenceYear: new Date().getFullYear(),
    hasFamilyFarmersMarket: null,
    numberOfFamilyFarmers: 0,
    annualMarketValue: 0,
    totalFamilyFarmingValue: 0,
    numberOfPermanentMarkets: 0,
    numberOfNonPermanentMarkets: 0,
};

// Step 3: Feiras Permanentes
const emptyStep3 = {
    referenceYear: new Date().getFullYear(),
    permanentMarkets: [],
};

// Step 4: Feiras Não Permanentes (renomeado para evitar conflito)
const emptyStep5FeirasNaoPermanentes = {
    referenceYear: new Date().getFullYear(),
    nonPermanentMarkets: [],
};

// Step 6: Informações do Informante
const emptyStep6 = {
    responsibleName: '',
    responsibleRole: '',
    responsibleContact: '',
};

export const FormularioSIMVisualizar: React.FC = () => {
    const { uuid } = useParams<{ uuid: string }>();
    const [activeStep, setActiveStep] = useState(0);
    const [step1Data, setStep1Data] = useState(emptyStep1);
    const [step2Data, setStep2Data] = useState(emptyStep2);
    const [step3Data, setStep3Data] = useState(emptyStep3); // Corrigido aqui!
    const [step4Data, setStep4Data] = useState(emptyStep5FeirasNaoPermanentes);
    const [step5Data, setStep5Data] = useState(emptyStep5Certificacao);
    const [step6Data, setStep6Data] = useState(emptyStep6);
    const [createdBy, setCreatedBy] = useState<string | null>(null);

    useEffect(() => {
        if (uuid) {
            fetchSimFormData(uuid).then((formData) => {
                setStep1Data(formData.step1);
                setStep2Data(formData.step2);
                setStep3Data(formData.step3);
                setStep4Data(formData.step4);
                setStep5Data(formData.step5);
                setStep6Data(formData.step6);
                setCreatedBy(formData.created_by || null);
            });
        }
    }, [uuid]);

    useEffect(() => {
        if (createdBy) {
            getListUsersByFilters({ limit: '1', uuid: createdBy }).then((res: any) => {
                const user = res?.data?.[0];
                setStep6Data({
                    responsibleName: user?.name || '',
                    responsibleRole: user?.position || user?.occupation || '',
                    responsibleContact: user?.contactList?.[0]?.value || '',
                });
            });
        }
    }, [createdBy]);

    const handleNext = () => setActiveStep((prev) => Math.min(prev + 1, 5));
    const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));

    // Sempre 6 etapas
    const totalSteps = 5;

    return (
        <ContainerPainel pagina="Visualizar Comercialização" showBackButton={true}>
            <Stack marginX={'4rem'} paddingTop={'2rem'}>
                <FormSimHorizontalStepper
                    activeStep={activeStep + 1}
                    hasFamilyFarmersMarket={step2Data.hasFamilyFarmersMarket}
                    edit={false}
                />

                <Box mt={2}>
                    {activeStep === 0 && <Step1SimView data={step1Data} />}
                    {activeStep === 1 && <Step2SimView data={step2Data} />}
                    {activeStep === 2 && <Step3SimView data={step3Data} />}
                    {activeStep === 3 && <Step4SimView data={step4Data} />}
                    {activeStep === 4 && <Step5SimView data={step5Data} />}
                </Box>

                <Box display="flex" justifyContent="space-between" mt={4} mb={2}>
                    <Button
                        variant="outlined"
                        onClick={handleBack}
                        disabled={activeStep === 0}
                    >
                        Voltar
                    </Button>
                    <span>
                        Aba {activeStep + 1} de {totalSteps}
                    </span>
                    <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={activeStep + 1 === totalSteps}
                    >
                        Próxima Aba
                    </Button>
                </Box>
            </Stack>
        </ContainerPainel>
    );
};

async function fetchSimFormData(uuid: string) {
    const data = await getFormularioSimByUUID(uuid);

    return {
        step1: {
            referenceYear: data?.reference_year ?? new Date().getFullYear(),
            hasSim: data?.has_sim ?? null,
            simActive: data?.sim_active ?? null,
            simEstablishments: data?.sim_establishments ?? 0,
            hasVet: data?.has_vet ?? null,
            joinedSusaf: data?.joined_susaf ?? null,
            wantsSusaf: data?.wants_susaf ?? null,
        },
        step2: {
            referenceYear: data?.reference_year ?? new Date().getFullYear(),
            hasFamilyFarmersMarket: data?.has_family_farmers_market ?? null,
            numberOfFamilyFarmers: data?.number_of_family_farmers ?? 0,
            annualMarketValue: data?.annual_market_value ?? 0,
            totalFamilyFarmingValue: parseFloat(data?.total_family_farming_value ?? 0),
            numberOfPermanentMarkets: data?.number_of_permanent_markets ?? 0,
            numberOfNonPermanentMarkets: data?.number_of_non_permanent_markets ?? 0,
        },
        step3: {
            referenceYear: data?.reference_year ?? new Date().getFullYear(),
            permanentMarkets: data?.permanent_markets ?? [],
        },
        step4: {
            referenceYear: data?.reference_year ?? new Date().getFullYear(),
            nonPermanentMarkets: data?.non_permanent_markets ?? [],
        },
        step5: {
            hasCertification: data?.has_certification ?? null,
            certificationSoldInFairs: data?.certification_sold_in_fairs ?? null,
            hasMunicipalSeal: data?.has_municipal_seal ?? null,
            municipalSealName: data?.municipal_seal_name ?? '',
            wantsMunicipalSeal: data?.wants_municipal_seal ?? null,
            knowsHowToCreateSeal: data?.knows_how_to_create_seal ?? null,
            referenceYear: data?.reference_year ?? new Date().getFullYear(),
        },
        step6: {
            responsibleName: '',
            responsibleRole: '',
            responsibleContact: '',
        },
        created_by: data?.created_by ?? '',
    };
}

export default FormularioSIMVisualizar;